import * as React from "react";
import {
  Box,
  Column,
  Columns,
  Divider,
  Hidden,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import PropertyType from "./FormComponents/PropertyType";
import { ReadOnlyServiceTypes } from "./FormComponents/ServiceTypes";
import { Field, useFormikContext } from "formik";
import FormContext from "src/context/FormContext";
import { AccountPayer, FormValues } from "./types";
import ReadOnlyPropertyAccessInPremise from "./ReadOnlyPropertyAccessInPremise";
import ReadOnlyTextField from "./FormComponents/ReadOnlyTextField";

const DBFormPremiseBlock = () => {
  const { values } = useFormikContext<FormValues>();
  const { formState } = React.useContext(FormContext);

  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        {/* Premise details */}
        <Columns collapseBelow="md">
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              <Columns collapseBelow="md">
                <Column>
                  <ReadOnlyTextField
                    label="Requestor's name"
                    value={values.electricityRequesterName ?? ""}
                  />
                </Column>
                <Column>
                  <ReadOnlyTextField
                    label="Requestor's email"
                    value={values.electricityRequesterEmail ?? ""}
                  />
                </Column>
              </Columns>

              <Field
                id="property-type"
                name="propertyType"
                component={PropertyType}
                isReadOnly={true}
              />
              {/* Address */}
              <Stack space="xsmall">
                <Stack space="xxsmall">
                  <Text variant="body-small" tone="neutralLight">
                    Address
                  </Text>
                  <Text>{formState.address}</Text>
                </Stack>
                <Divider />
              </Stack>
              {/* End Address */}
            </Stack>
          </Column>
          <Column width="1/3" />
        </Columns>

        {/* Safe access */}
        <Columns collapseBelow="md">
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              <ReadOnlyPropertyAccessInPremise />
            </Stack>
          </Column>
          <Column width="1/3">
            <Box display="flex" alignItems="stretch" height="full">
              <>
                <Hidden below="lg">
                  {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                  <Box
                    style={{ borderLeft: "1px solid #E3E3E3" }}
                    paddingRight="medium"
                    height="full"
                  />
                  {/* End Faux vertical <Divider> */}
                </Hidden>
                <Box
                  alignSelf="center"
                  paddingBottom={{
                    xs: "large",
                    lg: "none",
                  }}
                >
                  <Text variant="body-small" tone="neutralLight">
                    Clear access to the property and meter site is essential;
                    otherwise, rescheduling and fees may occur, impacting
                    installation timelines.
                  </Text>
                </Box>
              </>
            </Box>
          </Column>
        </Columns>
        {/* End Safe access */}

        {/* Services */}
        <Columns collapseBelow="md">
          <Column width="2/3">
            <Stack
              space={{
                xs: "large",
                md: "xlarge",
              }}
            >
              <ReadOnlyServiceTypes
                selectedConnections={values.selectedConnections}
              />
            </Stack>
          </Column>
        </Columns>
        {/* End Services */}
        {/* Account Payer */}
        <Columns
          collapseBelow="md"
          space={{
            xs: "none",
            md: "xlarge",
          }}
          alignY="stretch"
        >
          <Column width="2/3">
            <Stack space="xsmall">
              <Stack space="xxsmall">
                <Text variant="body-small" tone="neutralLight">
                  Who’s responsible for the electricity account?
                </Text>
                <Text>
                  {values.electricityAccountPayer === AccountPayer.SOMEONE_ELSE
                    ? "Customer"
                    : values.requestingCompany?.name}
                </Text>
              </Stack>
              <Divider />
            </Stack>
          </Column>
        </Columns>
        {/* End Account Payer */}
      </Stack>
      {/* End Premise section */}
    </Stack>
  );
};

export default DBFormPremiseBlock;
